import React, {
  createContext, useState, useContext, useMemo, useEffect, useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';

const PageHeaderContext = createContext(null);

export const PageHeaderProvider = ({ children, value: initValue = {} }) => {
  const {
    headerSettings: initHeaderSettings = {},
  } = initValue;

  const [headerSettings, setHeaderSettings] = useState(() => (initHeaderSettings));
  const [headerContent, setHeaderContent] = useState(null);
  const [headerRightSlot, setHeaderRightSlot] = useState(null);
  const [onLogoClick, setOnLogoClick] = useState();

  const value = useMemo(() => ({
    headerSettings,
    headerContent,
    headerRightSlot,
    onLogoClick,
    setHeaderContent,
    setOnLogoClick,
    setHeaderRightSlot,
    setHeaderSettings,
  }), [headerContent, headerRightSlot, headerSettings, onLogoClick]);

  return (
    <PageHeaderContext.Provider value={value}>
      {children}
    </PageHeaderContext.Provider>
  );
};

PageHeaderProvider.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.exact({
    headerSettings: PropTypes.shape({
      isShown: PropTypes.bool,
      isLogoShown: PropTypes.bool,
      isProfileButtonShown: PropTypes.bool,
      version: PropTypes.string,
      isLanguageMenuShown: PropTypes.bool,
      isLoginButtonShown: PropTypes.bool,
      isBurgerMenuShown: PropTypes.bool,
      isUploadButtonShown: PropTypes.bool,
      tabIds: PropTypes.array,
      backgroundColor: PropTypes.string,
      theme: PropTypes.string,
    }),
  }),
};

export const usePageHeader = () => useContext(PageHeaderContext);

export const useUpdatePageHeader = () => {
  const {
    setHeaderContent, setOnLogoClick, setHeaderSettings, setHeaderRightSlot,
  } = useContext(PageHeaderContext) ?? {};
  return {
    setHeaderContent, setOnLogoClick, setHeaderSettings, setHeaderRightSlot,
  };
};

export const useSetOnLogoClick = (onLogoClick) => {
  const { setOnLogoClick } = useUpdatePageHeader();

  useEffect(() => {
    // should set a function in order onLogoClick is not be called
    setOnLogoClick(() => onLogoClick);
    return () => {
      setOnLogoClick(null);
    };
  }, [setOnLogoClick, onLogoClick]);
};

export const useSetHeaderSettings = (settings) => {
  const { setHeaderSettings } = useUpdatePageHeader();
  const settingsHash = JSON.stringify(settings);

  useLayoutEffect(() => {
    setHeaderSettings(settings);
    return () => {
      setHeaderSettings({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsHash, setHeaderSettings]);
};
